/**
 * @file Colonne de boutons ronds lateraux : Armes, Skins, Renaissance, Missions, Modes, Cadeau du jour (pastilles de notification).
 */
import type { TranslationKey } from '@i18n/index';
import type { FeatureId } from '@config/unlocks';
import { dayKey } from '@core/index';
import { Component } from '../core/Component';
import { h, setText, toggleClass } from '../core/dom';
import { openScreen, send, uiHints } from '../core/feedback';
import { uiIcon } from '../core/uiIcons';
import type { UiIconId } from '../core/uiIcons';
import type { ScreenId, UiContext } from '../UiContext';

/** Le menu ne change pas assez vite pour justifier 30 Hz : 2 Hz suffisent (visibilite + pastilles). */
const PERIOD = 0.5;

interface Entry {
  id: string;
  screen: ScreenId;
  feature: FeatureId;
  label: TranslationKey;
  icon: UiIconId;
  /** Onglet a ouvrir (Missions). */
  tab?: 'daily';
  /** Action directe au lieu d'ouvrir un ecran (ex. entrer dans le hub). */
  action?: () => void;
  /** Compteur affiche a cote du bouton (ex. tickets de modes). */
  count?: () => number;
  countTitle?: TranslationKey;
  badge: () => boolean;
  /** Absent = toujours affichee ; sinon masquee tant que la fonction retourne faux (ex. serveur non configure). */
  visible?: () => boolean;
}

interface EntryView {
  def: Entry;
  root: HTMLElement;
  badge: HTMLElement;
  count: HTMLElement | null;
}

export class SideMenu extends Component {
  private readonly views: EntryView[] = [];
  private acc = PERIOD;

  /** `only` : identifiants des entrees affichees par cette colonne (le HUD repartit le menu a gauche / a droite). */
  constructor(
    ctx: UiContext,
    private readonly only?: readonly string[],
  ) {
    super(ctx, 'div', 'sidemenu');
  }

  protected build(): void {
    const { i18n, sim } = this.ctx;
    const claimable = (list: ReadonlyArray<{ progress: number; target: number; claimed: boolean }>): boolean => list.some((m) => !m.claimed && m.progress >= m.target);
    const entries: Entry[] = [
      {
        id: 'weapons',
        screen: 'weapons',
        feature: 'weapons',
        label: 'menu.weapons',
        icon: 'gun',
        badge: () => sim.data.weapons.dailyFreeDrawDay !== dayKey(Date.now()) || sim.query.weapons().some((w) => w.mergeable),
      },
      { id: 'skins', screen: 'skins', feature: 'skins', label: 'menu.skins', icon: 'cat', badge: () => false },
      { id: 'rebirth', screen: 'rebirth', feature: 'rebirth', label: 'menu.rebirth', icon: 'rebirth', badge: () => sim.query.rebirth().can },
      {
        id: 'missions',
        screen: 'missions',
        feature: 'missions',
        label: 'menu.missions',
        icon: 'missions',
        badge: () => claimable(sim.data.missions.daily) || claimable(sim.data.missions.weekly) || claimable(Object.values(sim.data.missions.achievements)),
      },
      {
        id: 'modes',
        screen: 'modes',
        feature: 'modeSelect',
        label: 'menu.modes',
        icon: 'map',
        badge: () => false,
        // Total des tickets de modes disponibles (SpeedRun, Mine, Boss, Raid, Aventure).
        count: () => Object.values(sim.data.tickets).reduce((n, t) => n + t.count, 0),
        countTitle: 'menu.tickets',
      },
      {
        id: 'hub',
        screen: 'modes',
        feature: 'hub',
        label: 'menu.hub',
        icon: 'hub',
        badge: () => false,
        action: () => void send(this.ctx, { type: 'mode.start', mode: 'hub' }),
      },
      {
        id: 'online',
        screen: 'online',
        feature: 'hub',
        label: 'menu.online',
        icon: 'globe',
        badge: () => (this.ctx.online?.badge ?? 0) > 0,
        visible: () => this.ctx.online !== undefined,
      },
      { id: 'daily', screen: 'missions', feature: 'daily', label: 'menu.daily', icon: 'gift', tab: 'daily', badge: () => sim.query.dailyAvailable() },
    ];
    const shown = this.only ? this.only.map((id) => entries.find((e) => e.id === id)).filter((e): e is Entry => e !== undefined) : entries;
    for (const def of shown) {
      const badge = h('span', { class: 'badge is-hidden' }, '!');
      const root = h(
        'button',
        {
          class: `menu-btn menu-${def.id}`,
          type: 'button',
          // nom accessible meme quand le libelle est masque (petits ecrans)
          'aria-label': i18n.t(def.label),
          'data-ui-block': true,
          'data-fast': true,
          'data-tutorial': `menu-${def.id === 'daily' ? 'daily' : def.screen}`,
          onclick: () => {
            if (def.action) {
              def.action();
              return;
            }
            if (def.tab) uiHints.missionsTab = def.tab;
            openScreen(def.screen);
          },
        },
        h('span', { class: 'menu-icon' }, uiIcon(def.icon)),
        h('span', { class: `menu-label${i18n.t(def.label).length > 9 ? ' long' : ''}` }, i18n.t(def.label)),
        badge,
      );
      // Compteur (tickets) : pastille posee contre le bouton, a sa gauche.
      let count: HTMLElement | null = null;
      if (def.count) {
        const countText = h('span', {}, '0');
        count = h('span', { class: 'menu-count', title: i18n.t(def.countTitle ?? 'menu.tickets') }, uiIcon('ticket'), countText);
        root.append(count);
      }
      this.views.push({ def, root, badge, count });
      this.el.append(root);
    }
    this.refresh();
  }

  private refresh(): void {
    const { query } = this.ctx.sim;
    for (const v of this.views) {
      const unlocked = query.feature(v.def.feature);
      toggleClass(v.root, 'is-hidden', !unlocked || (v.def.visible !== undefined && !v.def.visible()));
      toggleClass(v.badge, 'is-hidden', !unlocked || !v.def.badge());
      if (v.count && v.def.count) setText(v.count.lastElementChild as HTMLElement, String(v.def.count()));
    }
  }

  override update(dt: number): void {
    this.acc += dt;
    if (this.acc < PERIOD) return;
    this.acc = 0;
    this.refresh();
  }
}
